import React, { useRef, useState, useMemo, useEffect } from "react";
import ReactQuill from "react-quill";
import {
  MdOutlinePictureAsPdf,
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdOutlineFeaturedPlayList,
  MdSmartButton,
  MdLink,
  MdPictureAsPdf,
  MdClose,
  MdOutlineEditLocationAlt,
  MdDeleteOutline,
} from "react-icons/md";
import { FaFilePdf } from "react-icons/fa6";
import { Modal, Row, Col, Spinner } from "react-bootstrap";
import { uploadFileInChunks } from "../../../resources/utils/chunkUploader";
import RenderToast from "../../atoms/RenderToast/RenderToast";
import classes from "./QuillInput.module.css";

// Helper to clean URL and append PDF parameters that hide navigation sidebar and set zoom to 100%
const getPdfEmbedUrl = (url) => {
  if (!url) return "";
  const baseUrl = url.split("#")[0];
  return `${baseUrl}#toolbar=1&navpanes=0&pagemode=none&zoom=100`;
};

// Helper to generate consistent HTML block for PDF
const generatePdfHtml = ({ id, url, title, alignment = "center", displayStyle = "card" }) => {
  const safeId = id || `pdf-block-${Date.now()}`;
  const displayName = title?.trim() || "PDF Document";

  let innerContent = "";

  if (displayStyle === "card") {
    const textAlign = alignment === "full" ? "center" : alignment;
    const cardWidth = alignment === "full" ? "100%" : "auto";
    const maxWidth = alignment === "full" ? "100%" : "540px";
    const marginStyle =
      alignment === "center"
        ? "margin: 0 auto;"
        : alignment === "right"
        ? "margin-left: auto; margin-right: 0;"
        : "margin-right: auto; margin-left: 0;";

    innerContent = `
      <div style="text-align: ${textAlign}; width: 100%;">
        <div style="display: inline-block; text-align: left; background: #f8fafc; border: 1.5px solid #cbd5e1; border-radius: 12px; padding: 16px 22px; width: ${cardWidth}; max-width: ${maxWidth}; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.07); box-sizing: border-box; ${marginStyle}">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <span style="font-size: 30px; line-height: 1;">📄</span>
              <div>
                <p style="margin: 0; font-weight: 700; color: #0f172a; font-size: 16px; line-height: 1.3;">${displayName}</p>
                <p style="margin: 3px 0 0 0; font-size: 13px; color: #64748b; font-weight: 500;">PDF Document</p>
              </div>
            </div>
            <a href="${url}" target="_blank" rel="noopener noreferrer" style="background: #15803d; color: #ffffff !important; padding: 9px 18px; border-radius: 7px; text-decoration: none !important; font-weight: 600; font-size: 14px; display: inline-block; white-space: nowrap;">
              View / Download PDF
            </a>
          </div>
        </div>
      </div>
    `;
  } else if (displayStyle === "button") {
    const textAlign =
      alignment === "center"
        ? "center"
        : alignment === "right"
        ? "right"
        : "left";
    const btnWidth =
      alignment === "full"
        ? "width: 100%; text-align: center;"
        : "display: inline-block;";

    innerContent = `
      <p style="text-align: ${textAlign}; margin: 0; width: 100%;">
        <a href="${url}" target="_blank" rel="noopener noreferrer" style="background: #e11d48; color: #ffffff !important; padding: 11px 22px; border-radius: 8px; text-decoration: none !important; font-weight: 600; font-size: 14px; ${btnWidth} box-shadow: 0 4px 6px -1px rgba(225, 29, 72, 0.25);">
          📄 ${displayName}
        </a>
      </p>
    `;
  } else if (displayStyle === "embed") {
    const embedUrl = getPdfEmbedUrl(url);
    const width = alignment === "full" ? "100%" : "85%";
    const marginStyle =
      alignment === "center"
        ? "margin-left: auto; margin-right: auto; display: block;"
        : alignment === "right"
        ? "margin-left: auto; margin-right: 0; display: block;"
        : alignment === "full"
        ? "width: 100%; display: block;"
        : "margin-right: auto; margin-left: 0; display: block;";

    const textAlign =
      alignment === "center"
        ? "center"
        : alignment === "right"
        ? "right"
        : "left";

    innerContent = `
      <div style="text-align: ${textAlign}; width: 100%;">
        <iframe src="${embedUrl}" width="${width}" height="850px" style="border: 1.5px solid #cbd5e1; border-radius: 10px; max-width: 100%; min-height: 750px; box-shadow: 0 4px 10px rgba(0,0,0,0.08); ${marginStyle}" title="${displayName}"></iframe>
        <p style="margin-top: 8px; font-size: 13px; text-align: ${textAlign};">
          <a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #15803d; font-weight: 600; text-decoration: underline;">
            Open PDF in full screen
          </a>
        </p>
      </div>
    `;
  } else {
    // Simple inline link
    const textAlign =
      alignment === "center"
        ? "center"
        : alignment === "right"
        ? "right"
        : "left";

    innerContent = `
      <p style="text-align: ${textAlign}; margin: 0; width: 100%;">
        <a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #15803d; font-weight: 600; font-size: 15px; text-decoration: underline;">
          📄 ${displayName}
        </a>
      </p>
    `;
  }

  const containerAlign =
    alignment === "center"
      ? "text-align: center; display: flex; flex-direction: column; align-items: center;"
      : alignment === "right"
      ? "text-align: right; display: flex; flex-direction: column; align-items: flex-end;"
      : alignment === "full"
      ? "text-align: center; width: 100%;"
      : "text-align: left; display: flex; flex-direction: column; align-items: flex-start;";

  return `<div class="scalisi-pdf-attachment" data-pdf-id="${safeId}" data-pdf-url="${url}" data-pdf-title="${encodeURIComponent(
    displayName
  )}" data-pdf-style="${displayStyle}" data-pdf-align="${alignment}" style="margin: 20px 0; width: 100%; ${containerAlign}">${innerContent}</div>`;
};

// Helper to extract attached PDFs from HTML
const extractAttachedPdfs = (html) => {
  if (!html || typeof html !== "string") return [];
  const pdfs = [];

  if (typeof DOMParser !== "undefined") {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Check for structured attachments
      const blocks = doc.querySelectorAll(".scalisi-pdf-attachment, [data-pdf-id]");
      blocks.forEach((el, index) => {
        const id = el.getAttribute("data-pdf-id") || `pdf-block-${index}`;
        const url =
          el.getAttribute("data-pdf-url") ||
          el.querySelector("a")?.getAttribute("href") ||
          el.querySelector("iframe")?.getAttribute("src") ||
          "";
        const rawTitle = el.getAttribute("data-pdf-title");
        const title = rawTitle
          ? decodeURIComponent(rawTitle)
          : el.querySelector("p, strong, span")?.textContent?.replace("📄", "").trim() ||
            `PDF Document ${index + 1}`;
        const style =
          el.getAttribute("data-pdf-style") ||
          (el.querySelector("iframe") ? "embed" : "card");
        const align = el.getAttribute("data-pdf-align") || "center";

        if (url) {
          pdfs.push({
            id,
            url,
            title,
            displayStyle: style,
            alignment: align,
            html: el.outerHTML,
          });
        }
      });

      // Backward compatibility for unstructured PDF links / frames
      if (pdfs.length === 0) {
        const links = doc.querySelectorAll('a[href*=".pdf"], iframe[src*=".pdf"]');
        links.forEach((linkEl, index) => {
          const url = linkEl.getAttribute("href") || linkEl.getAttribute("src");
          const title =
            linkEl.textContent?.replace("📄", "").trim() ||
            `PDF Document ${index + 1}`;
          const isIframe = linkEl.tagName.toLowerCase() === "iframe";
          pdfs.push({
            id: `legacy-pdf-${index}`,
            url,
            title: title || "Attached PDF",
            displayStyle: isIframe ? "embed" : "card",
            alignment: "center",
            html: linkEl.parentElement?.outerHTML || linkEl.outerHTML,
            isLegacy: true,
          });
        });
      }
    } catch (err) {
      console.error("Error parsing attached PDFs:", err);
    }
  }

  return pdfs;
};

function QuillInput({
  value,
  setValue,
  quillClass = "",
  placeholder = "",
  label,
  disabled,
  isLoading,
  errorText,
}) {
  const quillRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // PDF Position & Style Configuration State
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingPdf, setPendingPdf] = useState(null);
  const [selectedPdfId, setSelectedPdfId] = useState("");
  const [pdfConfig, setPdfConfig] = useState({
    id: "",
    title: "",
    position: "cursor", // "cursor" | "top" | "bottom" | "keep"
    alignment: "center", // "left" | "center" | "right" | "full"
    displayStyle: "card", // "card" | "button" | "embed" | "link"
  });

  // Detect currently attached PDFs in editor content
  const attachedPdfs = useMemo(() => {
    return extractAttachedPdfs(value);
  }, [value]);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link", "image"],
        ["clean"],
      ],
    }),
    []
  );

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      RenderToast({
        type: "error",
        message: "Please select a valid PDF file (.pdf)",
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const pdfUrl = await uploadFileInChunks({
        file,
        onProgress: (percent) => {
          setUploadProgress(percent);
        },
      });

      if (pdfUrl) {
        const cleanTitle = file.name.replace(/\.[^/.]+$/, "");
        const newId = `pdf-${Date.now()}`;
        setPendingPdf({
          id: newId,
          url: pdfUrl,
          fileName: file.name,
          fileSize: file.size,
        });
        setPdfConfig({
          id: newId,
          title: cleanTitle,
          position: "cursor",
          alignment: "center",
          displayStyle: "card",
        });
        setIsEditMode(false);
        setShowConfigModal(true);
      }
    } catch (err) {
      console.error("PDF upload error:", err);
      RenderToast({
        type: "error",
        message: err.message || "Failed to upload PDF file",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (pdfInputRef.current) {
        pdfInputRef.current.value = "";
      }
    }
  };

  // Open Edit Modal for attached PDF
  const handleOpenEditPdfModal = (pdfToEdit) => {
    const targetPdf = pdfToEdit || attachedPdfs[0];
    if (!targetPdf) return;

    setSelectedPdfId(targetPdf.id);
    setPendingPdf({
      id: targetPdf.id,
      url: targetPdf.url,
      fileName: targetPdf.title,
      html: targetPdf.html,
    });
    setPdfConfig({
      id: targetPdf.id,
      title: targetPdf.title,
      position: "keep",
      alignment: targetPdf.alignment || "center",
      displayStyle: targetPdf.displayStyle || "card",
    });
    setIsEditMode(true);
    setShowConfigModal(true);
  };

  // Switch selected PDF when editing
  const handlePdfSelectionChange = (pdfId) => {
    const found = attachedPdfs.find((p) => p.id === pdfId);
    if (found) {
      setSelectedPdfId(found.id);
      setPendingPdf({
        id: found.id,
        url: found.url,
        fileName: found.title,
        html: found.html,
      });
      setPdfConfig({
        id: found.id,
        title: found.title,
        position: "keep",
        alignment: found.alignment || "center",
        displayStyle: found.displayStyle || "card",
      });
    }
  };

  // Delete an attached PDF from editor content
  const handleDeletePdf = () => {
    if (!pendingPdf) return;

    const editor = quillRef.current?.getEditor();
    let currentHtml = value || (editor ? editor.root.innerHTML : "") || "";

    // Remove by matching HTML block or ID attribute
    if (pendingPdf.html && currentHtml.includes(pendingPdf.html)) {
      currentHtml = currentHtml.replace(pendingPdf.html, "");
    } else {
      // Fallback DOM removal
      const parser = new DOMParser();
      const doc = parser.parseFromString(currentHtml, "text/html");
      const elToRemove =
        doc.querySelector(`[data-pdf-id="${pendingPdf.id}"]`) ||
        doc.querySelector(`a[href="${pendingPdf.url}"]`)?.closest(".scalisi-pdf-attachment, p, div") ||
        doc.querySelector(`iframe[src="${pendingPdf.url}"]`)?.closest(".scalisi-pdf-attachment, p, div");
      if (elToRemove) {
        elToRemove.remove();
        currentHtml = doc.body.innerHTML;
      }
    }

    if (editor) {
      editor.clipboard.dangerouslyPasteHTML(currentHtml);
    }
    setValue(currentHtml);

    setShowConfigModal(false);
    setPendingPdf(null);
    RenderToast({
      type: "success",
      message: "PDF removed from article successfully.",
    });
  };

  // Save new or edited PDF
  const handleSavePdf = () => {
    if (!pendingPdf?.url) return;

    const editor = quillRef.current?.getEditor();
    const currentHtml = value || (editor ? editor.root.innerHTML : "") || "";

    const { id, title, position, alignment, displayStyle } = pdfConfig;
    const displayName = title?.trim() || pendingPdf.fileName;
    const pdfUrl = pendingPdf.url;
    const blockId = id || pendingPdf.id || `pdf-${Date.now()}`;

    const newPdfHtml = generatePdfHtml({
      id: blockId,
      url: pdfUrl,
      title: displayName,
      alignment,
      displayStyle,
    });

    if (isEditMode) {
      // Editing existing PDF
      let updatedHtml = currentHtml;

      if (position === "top") {
        // Remove old and prepend to top
        if (pendingPdf.html && updatedHtml.includes(pendingPdf.html)) {
          updatedHtml = updatedHtml.replace(pendingPdf.html, "");
        } else {
          const parser = new DOMParser();
          const doc = parser.parseFromString(updatedHtml, "text/html");
          doc.querySelector(`[data-pdf-id="${pendingPdf.id}"]`)?.remove();
          updatedHtml = doc.body.innerHTML;
        }
        updatedHtml = `${newPdfHtml}<br/>${updatedHtml}`;
      } else if (position === "bottom") {
        // Remove old and append to bottom
        if (pendingPdf.html && updatedHtml.includes(pendingPdf.html)) {
          updatedHtml = updatedHtml.replace(pendingPdf.html, "");
        } else {
          const parser = new DOMParser();
          const doc = parser.parseFromString(updatedHtml, "text/html");
          doc.querySelector(`[data-pdf-id="${pendingPdf.id}"]`)?.remove();
          updatedHtml = doc.body.innerHTML;
        }
        updatedHtml = `${updatedHtml}<br/>${newPdfHtml}`;
      } else {
        // Keep in place, update block
        if (pendingPdf.html && updatedHtml.includes(pendingPdf.html)) {
          updatedHtml = updatedHtml.replace(pendingPdf.html, newPdfHtml);
        } else {
          const parser = new DOMParser();
          const doc = parser.parseFromString(updatedHtml, "text/html");
          const targetEl =
            doc.querySelector(`[data-pdf-id="${pendingPdf.id}"]`) ||
            doc.querySelector(`a[href="${pendingPdf.url}"]`)?.closest(".scalisi-pdf-attachment, p, div");
          if (targetEl) {
            targetEl.outerHTML = newPdfHtml;
            updatedHtml = doc.body.innerHTML;
          } else {
            updatedHtml = `${updatedHtml}<br/>${newPdfHtml}`;
          }
        }
      }

      if (editor) {
        editor.clipboard.dangerouslyPasteHTML(updatedHtml);
      }
      setValue(updatedHtml);

      RenderToast({
        type: "success",
        message: "PDF position & style updated successfully!",
      });
    } else {
      // Inserting newly uploaded PDF
      if (editor) {
        let insertIndex = editor.getLength();
        if (position === "cursor") {
          const range = editor.getSelection(true);
          insertIndex = range ? range.index : editor.getLength() - 1;
        } else if (position === "top") {
          insertIndex = 0;
        } else if (position === "bottom") {
          insertIndex = editor.getLength();
        }

        editor.clipboard.dangerouslyPasteHTML(insertIndex, newPdfHtml);
        setTimeout(() => {
          if (editor) {
            setValue(editor.root.innerHTML);
          }
        }, 0);
      } else {
        const updated = `${currentHtml}<br/>${newPdfHtml}`;
        setValue(updated);
      }

      RenderToast({
        type: "success",
        message: "PDF positioned and inserted into content successfully!",
      });
    }

    setShowConfigModal(false);
    setPendingPdf(null);
  };

  return (
    <div className={classes.quillContainer}>
      <div className={classes.headerRow}>
        {label && <label className={classes.label}>{label}</label>}

        <div className={classes.actionBtnsGroup}>
          {attachedPdfs.length > 0 && (
            <button
              type="button"
              className={classes.editPdfBtn}
              onClick={() => handleOpenEditPdfModal()}
              disabled={disabled || isUploading}
              title="Edit position, alignment, display style or title of attached PDF"
            >
              <MdOutlineEditLocationAlt size={17} />
              <span>
                Edit PDF Position ({attachedPdfs.length})
              </span>
            </button>
          )}

          <button
            type="button"
            className={classes.attachPdfBtn}
            onClick={() => pdfInputRef.current?.click()}
            disabled={disabled || isUploading}
            title="Upload and position a new PDF document in editor"
          >
            <MdOutlinePictureAsPdf size={18} className={classes.pdfIcon} />
            <span>
              {isUploading
                ? `Uploading PDF (${uploadProgress}%)...`
                : "Attach & Position PDF"}
            </span>
            {isUploading && (
              <Spinner animation="border" size="sm" className="ms-1" />
            )}
          </button>
        </div>
      </div>

      {isUploading && (
        <div className={classes.progressContainer}>
          <div
            className={classes.progressBar}
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      <input
        type="file"
        ref={pdfInputRef}
        accept="application/pdf,.pdf"
        onChange={handlePdfUpload}
        hidden
      />

      <div className={classes.quillInput}>
        <ReactQuill
          ref={quillRef}
          className={`${classes.quill} ${quillClass}`}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => setValue(e)}
          modules={modules}
          readOnly={disabled || isUploading}
        />
      </div>

      {errorText && (
        <p className={`mt-2 text-danger ${[classes.errorText].join(" ")}`}>
          {errorText}
        </p>
      )}

      {/* PDF Positioning & Styling Modal */}
      <Modal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        centered
        size="lg"
        className={classes.configModal}
      >
        <Modal.Header className={classes.modalHeader}>
          <div className="d-flex align-items-center gap-2">
            <FaFilePdf size={22} color="#e11d48" />
            <h5 className="m-0 fw-700 text-dark">
              {isEditMode ? "Edit PDF Position & Style" : "Position & Style PDF"}
            </h5>
          </div>
          <button
            type="button"
            className={classes.closeBtn}
            onClick={() => setShowConfigModal(false)}
          >
            <MdClose size={20} />
          </button>
        </Modal.Header>

        <Modal.Body className={classes.modalBody}>
          <Row className="g-4">
            {/* Multiple PDF selector when editing */}
            {isEditMode && attachedPdfs.length > 1 && (
              <Col md={12}>
                <label className={classes.sectionLabel}>Select PDF to Edit</label>
                <select
                  className={classes.pdfSelectBox}
                  value={selectedPdfId}
                  onChange={(e) => handlePdfSelectionChange(e.target.value)}
                >
                  {attachedPdfs.map((pdf, idx) => (
                    <option key={pdf.id || idx} value={pdf.id}>
                      📄 {pdf.title} ({pdf.displayStyle})
                    </option>
                  ))}
                </select>
              </Col>
            )}

            {/* Title / Label Input */}
            <Col md={12}>
              <label className={classes.sectionLabel}>Display Title / Label</label>
              <input
                type="text"
                className={classes.customInput}
                value={pdfConfig.title}
                onChange={(e) =>
                  setPdfConfig({ ...pdfConfig, title: e.target.value })
                }
                placeholder="e.g. Weekly Produce Catalog (PDF)"
              />
            </Col>

            {/* Placement in Content */}
            <Col md={6}>
              <label className={classes.sectionLabel}>Position in Article</label>
              <div className={classes.optionGroup}>
                {isEditMode ? (
                  <>
                    <button
                      type="button"
                      className={`${classes.optionCard} ${
                        pdfConfig.position === "keep" ? classes.activeOption : ""
                      }`}
                      onClick={() =>
                        setPdfConfig({ ...pdfConfig, position: "keep" })
                      }
                    >
                      <span className="fw-600">📍 Keep Current Position</span>
                      <small className="text-muted">
                        Stay in place, update alignment / style
                      </small>
                    </button>
                    <button
                      type="button"
                      className={`${classes.optionCard} ${
                        pdfConfig.position === "top" ? classes.activeOption : ""
                      }`}
                      onClick={() =>
                        setPdfConfig({ ...pdfConfig, position: "top" })
                      }
                    >
                      <span className="fw-600">⬆️ Move to Top of Article</span>
                      <small className="text-muted">Relocate to the beginning</small>
                    </button>
                    <button
                      type="button"
                      className={`${classes.optionCard} ${
                        pdfConfig.position === "bottom"
                          ? classes.activeOption
                          : ""
                      }`}
                      onClick={() =>
                        setPdfConfig({ ...pdfConfig, position: "bottom" })
                      }
                    >
                      <span className="fw-600">⬇️ Move to Bottom of Article</span>
                      <small className="text-muted">Relocate to the very end</small>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={`${classes.optionCard} ${
                        pdfConfig.position === "cursor"
                          ? classes.activeOption
                          : ""
                      }`}
                      onClick={() =>
                        setPdfConfig({ ...pdfConfig, position: "cursor" })
                      }
                    >
                      <span className="fw-600">📌 Current Cursor</span>
                      <small className="text-muted">Insert at cursor position</small>
                    </button>
                    <button
                      type="button"
                      className={`${classes.optionCard} ${
                        pdfConfig.position === "top" ? classes.activeOption : ""
                      }`}
                      onClick={() =>
                        setPdfConfig({ ...pdfConfig, position: "top" })
                      }
                    >
                      <span className="fw-600">⬆️ Top of Article</span>
                      <small className="text-muted">Insert at the beginning</small>
                    </button>
                    <button
                      type="button"
                      className={`${classes.optionCard} ${
                        pdfConfig.position === "bottom"
                          ? classes.activeOption
                          : ""
                      }`}
                      onClick={() =>
                        setPdfConfig({ ...pdfConfig, position: "bottom" })
                      }
                    >
                      <span className="fw-600">⬇️ Bottom of Article</span>
                      <small className="text-muted">Insert at the very end</small>
                    </button>
                  </>
                )}
              </div>
            </Col>

            {/* Alignment */}
            <Col md={6}>
              <label className={classes.sectionLabel}>Alignment</label>
              <div className={classes.alignmentGroup}>
                <button
                  type="button"
                  className={`${classes.alignCard} ${
                    pdfConfig.alignment === "left" ? classes.activeOption : ""
                  }`}
                  onClick={() =>
                    setPdfConfig({ ...pdfConfig, alignment: "left" })
                  }
                >
                  <MdFormatAlignLeft size={20} />
                  <span>Left</span>
                </button>
                <button
                  type="button"
                  className={`${classes.alignCard} ${
                    pdfConfig.alignment === "center" ? classes.activeOption : ""
                  }`}
                  onClick={() =>
                    setPdfConfig({ ...pdfConfig, alignment: "center" })
                  }
                >
                  <MdFormatAlignCenter size={20} />
                  <span>Center</span>
                </button>
                <button
                  type="button"
                  className={`${classes.alignCard} ${
                    pdfConfig.alignment === "right" ? classes.activeOption : ""
                  }`}
                  onClick={() =>
                    setPdfConfig({ ...pdfConfig, alignment: "right" })
                  }
                >
                  <MdFormatAlignRight size={20} />
                  <span>Right</span>
                </button>
                <button
                  type="button"
                  className={`${classes.alignCard} ${
                    pdfConfig.alignment === "full" ? classes.activeOption : ""
                  }`}
                  onClick={() =>
                    setPdfConfig({ ...pdfConfig, alignment: "full" })
                  }
                >
                  <span className="fw-700">100%</span>
                  <span>Full Width</span>
                </button>
              </div>
            </Col>

            {/* Display Styles */}
            <Col md={12}>
              <label className={classes.sectionLabel}>Display Style</label>
              <Row className="g-3">
                <Col sm={6} md={3}>
                  <button
                    type="button"
                    className={`${classes.styleCard} ${
                      pdfConfig.displayStyle === "card"
                        ? classes.activeOption
                        : ""
                    }`}
                    onClick={() =>
                      setPdfConfig({ ...pdfConfig, displayStyle: "card" })
                    }
                  >
                    <MdOutlineFeaturedPlayList size={24} className="text-primary" />
                    <span className="fw-600">Download Card</span>
                    <small className="text-muted text-center">
                      Interactive boxed card with icon & download button
                    </small>
                  </button>
                </Col>
                <Col sm={6} md={3}>
                  <button
                    type="button"
                    className={`${classes.styleCard} ${
                      pdfConfig.displayStyle === "button"
                        ? classes.activeOption
                        : ""
                    }`}
                    onClick={() =>
                      setPdfConfig({ ...pdfConfig, displayStyle: "button" })
                    }
                  >
                    <MdSmartButton size={24} className="text-danger" />
                    <span className="fw-600">Action Button</span>
                    <small className="text-muted text-center">
                      Eye-catching clickable CTA button
                    </small>
                  </button>
                </Col>
                <Col sm={6} md={3}>
                  <button
                    type="button"
                    className={`${classes.styleCard} ${
                      pdfConfig.displayStyle === "embed"
                        ? classes.activeOption
                        : ""
                    }`}
                    onClick={() =>
                      setPdfConfig({ ...pdfConfig, displayStyle: "embed" })
                    }
                  >
                    <MdPictureAsPdf size={24} className="text-warning" />
                    <span className="fw-600">Embedded Viewer</span>
                    <small className="text-muted text-center">
                      Embedded readable PDF preview frame
                    </small>
                  </button>
                </Col>
                <Col sm={6} md={3}>
                  <button
                    type="button"
                    className={`${classes.styleCard} ${
                      pdfConfig.displayStyle === "link"
                        ? classes.activeOption
                        : ""
                    }`}
                    onClick={() =>
                      setPdfConfig({ ...pdfConfig, displayStyle: "link" })
                    }
                  >
                    <MdLink size={24} className="text-success" />
                    <span className="fw-600">Text Link</span>
                    <small className="text-muted text-center">
                      Clean inline hyperlink
                    </small>
                  </button>
                </Col>
              </Row>
            </Col>

            {/* Live Preview Box */}
            <Col md={12}>
              <label className={classes.sectionLabel}>Live Preview</label>
              <div className={classes.previewContainer}>
                <div
                  style={{
                    textAlign:
                      pdfConfig.alignment === "full"
                        ? "center"
                        : pdfConfig.alignment,
                  }}
                >
                  {pdfConfig.displayStyle === "card" && (
                    <div
                      style={{
                        display: "inline-block",
                        textAlign: "left",
                        background: "#f8fafc",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "10px",
                        padding: "12px 18px",
                        width: pdfConfig.alignment === "full" ? "100%" : "auto",
                        maxWidth: "500px",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: "24px" }}>📄</span>
                          <div>
                            <p className="m-0 fw-700 text-dark fs-14">
                              {pdfConfig.title || pendingPdf?.fileName}
                            </p>
                            <p className="m-0 text-muted fs-12">PDF Document</p>
                          </div>
                        </div>
                        <span className="badge bg-success py-2 px-3 fs-12 fw-600">
                          View / Download PDF
                        </span>
                      </div>
                    </div>
                  )}

                  {pdfConfig.displayStyle === "button" && (
                    <button
                      type="button"
                      className="btn btn-danger py-2 px-4 fw-600"
                      style={{
                        width:
                          pdfConfig.alignment === "full" ? "100%" : "auto",
                      }}
                    >
                      📄 {pdfConfig.title || pendingPdf?.fileName}
                    </button>
                  )}

                  {pdfConfig.displayStyle === "embed" && (
                    <div
                      style={{
                        background: "#f1f5f9",
                        border: "1.5px dashed #94a3b8",
                        borderRadius: "8px",
                        height: "120px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width:
                          pdfConfig.alignment === "full" ? "100%" : "80%",
                        margin: "0 auto",
                      }}
                    >
                      <span className="text-muted fw-600">
                        📄 Interactive PDF Viewer:{" "}
                        {pdfConfig.title || pendingPdf?.fileName}
                      </span>
                    </div>
                  )}

                  {pdfConfig.displayStyle === "link" && (
                    <span className="text-success fw-600 text-decoration-underline fs-15">
                      📄 {pdfConfig.title || pendingPdf?.fileName}
                    </span>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className={classes.modalFooter}>
          <div className="d-flex align-items-center justify-content-between w-100">
            {isEditMode ? (
              <button
                type="button"
                className={classes.deleteBtn}
                onClick={handleDeletePdf}
              >
                <MdDeleteOutline size={18} />
                <span>Remove PDF</span>
              </button>
            ) : (
              <div />
            )}

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                onClick={() => setShowConfigModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 py-2 fw-600"
                onClick={handleSavePdf}
              >
                {isEditMode ? "Update PDF Position & Style" : "Insert PDF into Content"}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default QuillInput;

